import React, { useState } from 'react';
import Navigation from './Navigation';
import "./Dashboard.css";

const Dashboard = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [callRemark, setCallRemark] = useState('');
  const [callStatus, setCallStatus] = useState('');

  // Original data
  const [originalData, setOriginalData] = useState([
    { 
      id: 1, 
      name: 'Alice Johnson', 
      phone: '+1 (555) 123-4567', 
      email: 'alice.johnson@email.com',
      company: 'Tech Solutions Inc',
      status: 'new',
      priority: 'high',
      callCount: 0,
      lastCalled: '',
      remarks: []
    },
    { 
      id: 2, 
      name: 'Mike Wilson', 
      phone: '+1 (555) 234-5678', 
      email: 'mike.wilson@company.com',
      company: 'Digital Dynamics',
      status: 'contacted',
      priority: 'medium',
      callCount: 2,
      lastCalled: '2024-03-15 10:30 AM',
      remarks: []
    },
    { 
      id: 3, 
      name: 'Sarah Davis', 
      phone: '+1 (555) 345-6789', 
      email: 'sarah.davis@business.com',
      company: 'Global Manufacturing',
      status: 'interested',
      priority: 'high',
      callCount: 3,
      lastCalled: '2024-03-14 3:45 PM',
      remarks: []
    },
    { 
      id: 4, 
      name: 'Robert Brown', 
      phone: '+1 (555) 456-7890', 
      email: 'robert.brown@corp.com',
      company: 'Innovation Labs',
      status: 'not_interested',
      priority: 'low',
      callCount: 1,
      lastCalled: '2024-03-13 4:20 PM',
      remarks: []
    },
    { 
      id: 5, 
      name: 'Emily Chen', 
      phone: '+1 (555) 567-8901', 
      email: 'emily.chen@startup.com',
      company: 'Future Tech',
      status: 'callback',
      priority: 'medium',
      callCount: 1,
      lastCalled: '2024-03-15 1:15 PM',
      remarks: []
    }
  ]);

  // Working data (what user sees and edits)
  const [workingData, setWorkingData] = useState([...originalData]);

  // Track which customers have changes
  const [hasChanges, setHasChanges] = useState({});

  const stats = {
    totalContacts: originalData.length,
    callsMade: originalData.reduce((sum, contact) => sum + contact.callCount, 0),
    successfulCalls: originalData.filter(c => c.status === 'interested').length,
    pendingCalls: originalData.filter(c => ['new', 'contacted', 'callback'].includes(c.status)).length
  };

  const filteredContacts = workingData.filter(contact => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'new') return contact.status === 'new';
    if (selectedFilter === 'pending') return ['new', 'contacted', 'callback'].includes(contact.status);
    if (selectedFilter === 'interested') return contact.status === 'interested';
    return contact.status === selectedFilter;
  });

  // Handle field changes
  const handleFieldChange = (contactId, field, value) => {
    setWorkingData(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, [field]: value }
          : contact
      )
    );

    // Mark this customer as having changes
    setHasChanges(prev => ({
      ...prev,
      [contactId]: true
    }));
  };

  // Update customer data
  const handleUpdateCustomer = (contactId) => {
    const workingContact = workingData.find(c => c.id === contactId);
    
    if (!workingContact) return;

    // Update original data
    setOriginalData(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...workingContact }
          : contact
      )
    );

    // Clear changes flag
    setHasChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[contactId];
      return newChanges;
    });

    // Log for backend integration
    console.log('Customer updated:', {
      contactId,
      data: workingContact,
      timestamp: new Date().toISOString()
    });

    alert('Customer data updated successfully!');
  };

  // Reset changes for a customer
  const handleResetCustomer = (contactId) => {
    const originalContact = originalData.find(c => c.id === contactId);
    
    if (!originalContact) return;

    setWorkingData(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...originalContact }
          : contact
      )
    );

    // Clear changes flag
    setHasChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[contactId];
      return newChanges;
    });
  };

  const handleCallCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCallModal(true);
    setCallRemark('');
    setCallStatus('');
  };

  const handleSubmitRemark = () => {
    if (callRemark.trim() && callStatus) {
      console.log('Submitting remark:', {
        customerId: selectedCustomer.id,
        status: callStatus,
        remark: callRemark,
        timestamp: new Date().toISOString()
      });
      
      setShowCallModal(false);
      setSelectedCustomer(null);
      setCallRemark('');
      setCallStatus('');
      
      alert('Remark added successfully!');
    }
  };

  return (
    <div className="employee-dashboard">
      <Navigation />
      
      <div className="dashboard-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-info">
              <h3>Total Contacts</h3>
              <div className="stat-value">{stats.totalContacts}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📞</div>
            <div className="stat-info">
              <h3>Calls Made</h3>
              <div className="stat-value">{stats.callsMade}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>Successful</h3>
              <div className="stat-value">{stats.successfulCalls}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <h3>Pending</h3>
              <div className="stat-value">{stats.pendingCalls}</div>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="table-container">
          <div className="table-header">
            <h2>Customer Contacts</h2>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('all')}
              >
                All ({workingData.length})
              </button>
              <button 
                className={`filter-btn ${selectedFilter === 'new' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('new')}
              >
                New ({workingData.filter(c => c.status === 'new').length})
              </button>
              <button 
                className={`filter-btn ${selectedFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('pending')}
              >
                Pending ({workingData.filter(c => ['new', 'contacted', 'callback'].includes(c.status)).length})
              </button>
              <button 
                className={`filter-btn ${selectedFilter === 'interested' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('interested')}
              >
                Interested ({workingData.filter(c => c.status === 'interested').length})
              </button>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Phone</th>
                  <th>Email ✏️</th>
                  <th>Status ✏️</th>
                  <th>Priority ✏️</th>
                  <th>Calls ✏️</th>
                  <th>Last Called ✏️</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className={hasChanges[contact.id] ? 'row-modified' : ''}>
                    <td>
                      <div className="contact-name">
                        <strong>{contact.name}</strong>
                        {hasChanges[contact.id] && <span className="modified-badge">Modified</span>}
                      </div>
                    </td>
                    <td>{contact.company}</td>
                    <td>
                      <a href={`tel:${contact.phone}`} className="phone-link">
                        {contact.phone}
                      </a>
                    </td>
                    <td>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleFieldChange(contact.id, 'email', e.target.value)}
                        className="table-input"
                      />
                    </td>
                    <td>
                      <select
                        value={contact.status}
                        onChange={(e) => handleFieldChange(contact.id, 'status', e.target.value)}
                        className="table-select"
                      >
                        <option value="new">New Lead</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="not_interested">Not Interested</option>
                        <option value="callback">Callback Requested</option>
                        <option value="busy">Busy</option>
                        <option value="no_answer">No Answer</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={contact.priority}
                        onChange={(e) => handleFieldChange(contact.id, 'priority', e.target.value)}
                        className="table-select"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={contact.callCount}
                        onChange={(e) => handleFieldChange(contact.id, 'callCount', parseInt(e.target.value) || 0)}
                        className="table-input number-input"
                        min="0"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={contact.lastCalled}
                        onChange={(e) => handleFieldChange(contact.id, 'lastCalled', e.target.value)}
                        className="table-input"
                        placeholder="Never"
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="call-btn"
                          onClick={() => handleCallCustomer(contact)}
                        >
                          📞 Call
                        </button>
                        <button className="remarks-btn">
                          📝 Remarks ({contact.remarks.length})
                        </button>
                        {hasChanges[contact.id] && (
                          <>
                            <button 
                              className="update-btn"
                              onClick={() => handleUpdateCustomer(contact.id)}
                            >
                              💾 Update
                            </button>
                            <button 
                              className="reset-btn"
                              onClick={() => handleResetCustomer(contact.id)}
                            >
                              ↺ Reset
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Call Modal */}
      {showCallModal && (
        <div className="modal-overlay">
          <div className="call-modal">
            <div className="modal-header">
              <h3>Add Call Remark</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCallModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="customer-info-modal">
                <h4>{selectedCustomer?.name}</h4>
                <p>{selectedCustomer?.phone}</p>
                <p>{selectedCustomer?.company}</p>
              </div>
              
              <div className="form-group">
                <label>Call Status:</label>
                <select 
                  value={callStatus} 
                  onChange={(e) => setCallStatus(e.target.value)}
                  className="status-select"
                >
                  <option value="">Select Status</option>
                  <option value="interested">Interested</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="callback">Callback Requested</option>
                  <option value="busy">Busy</option>
                  <option value="no_answer">No Answer</option>
                  <option value="contacted">General Contact</option>
                </select>
              </div>

              <div className="form-group">
                <label>Remark/Notes:</label>
                <textarea
                  value={callRemark}
                  onChange={(e) => setCallRemark(e.target.value)}
                  placeholder="Enter your notes about the call..."
                  className="remark-textarea"
                  rows="4"
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowCallModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="submit-btn"
                  onClick={handleSubmitRemark}
                  disabled={!callRemark.trim() || !callStatus}
                >
                  Add Remark
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;   


